/**
 * How the palette matches what you type. Every word you type must start a word of the title
 * or of its keywords ("stop" finds "Stop session", never "Go to Settings" just because the
 * letters s, t, o and p appear in order). A word of three letters or more may also sit inside
 * a title word ("approv" finds "Auto-approve"). Title hits outrank keyword hits.
 */

/** Separates an item's title from its unique id in a cmdk value. */
const SEP = '⁣';

/** The cmdk value for an item: its title first (what is matched), then its id (what keeps it unique). */
export function itemValue(title: string, id: string): string {
  return `${title}${SEP}${id}`;
}

/** The title part of a cmdk value built by itemValue (the whole value otherwise). */
export function valueTitle(value: string): string {
  const i = value.indexOf(SEP);
  return i >= 0 ? value.slice(0, i) : value;
}

export function words(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/**
 * 0 when some typed word matches nothing; otherwise up to 0.95, and 1 for exactly the
 * title. An empty query matches everything with 1.
 */
export function matchScore(query: string, title: string, keywords: readonly string[] = []): number {
  const q = words(query);
  if (!q.length) return 1;
  const tw = words(title);
  const kw = keywords.flatMap(words);
  const lowTitle = title.toLowerCase();
  let total = 0;
  for (const w of q) {
    let s = 0;
    if (tw.includes(w)) s = 1;
    else if (tw.some((t) => t.startsWith(w))) s = 0.85;
    else if (kw.includes(w)) s = 0.7;
    else if (kw.some((k) => k.startsWith(w))) s = 0.6;
    else if (w.length >= 3 && lowTitle.includes(w)) s = 0.5;
    if (!s) return 0;
    total += s;
  }
  // Exactly the title ("Add machine"): the top score, above an answer in words (0.97), so
  // Enter runs the command named rather than a filter read from the same words.
  if (words(title).join(' ') === q.join(' ')) return 1;
  let score = total / q.length;
  // The title starts with what was typed: "new i" puts New item first.
  if (lowTitle.startsWith(query.trim().toLowerCase())) score = Math.min(1, score + 0.1);
  return score * 0.95;
}

/** The best `limit` entries whose text matches, best first; ties keep their order. */
export function topMatches<T>(list: readonly T[], query: string, text: (t: T) => { title: string; keywords?: readonly string[] }, limit: number): T[] {
  if (!words(query).length) return [];
  return list
    .map((t, i) => {
      const x = text(t);
      return { t, i, s: matchScore(query, x.title, x.keywords) };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.t);
}

/** The text is exactly one of the titles, word for word (case and spacing aside). */
export function isCommandTitle(text: string, titles: readonly string[]): boolean {
  const t = words(text).join(' ');
  return !!t && titles.some((x) => words(x).join(' ') === t);
}
